// Modal-style dialog primitive (phase 2.1.5). Replaces 15+ inline Modal +
// backdrop + content-card hand-rolls across the codebase.
//
// Default chrome lifted from MyPassport's BadgeDetailPanel (the modal that
// reads as the most polished one in the app):
//   - Frosted-glass container (backdrop-filter blur on web,
//     <BlurView intensity={80} tint="light"> on native).
//   - 40% black backdrop overlay (lighter than the old 50%).
//   - 20px radius, generous padding (28 H × 40 top / 32 bottom).
//   - Optional close-button affordance in the top-right corner.
//
// Two layouts:
//   - "centered": alert / confirmation. Fade animation, 85%-width centered.
//     Use for LogoutModal, DeleteAccountModal, Disable2FAModal, badge details,
//     etc.
//   - "bottom":   anchored to bottom edge. Slide animation, top corners
//     rounded. Use for InsuranceInfoModal, IntentPickerModal, etc.
//
// Important: this is a **modal**, not a real native sheet. A real sheet
// (iOS-style with drag handle, snap points, gesture-driven dismiss,
// partial reveal) is reserved as <BottomSheet>, not yet built.
//
// Backdrop tap dismisses by default. Pass `dismissOnBackdropTap={false}`
// for required-action dialogs.
//
// Caller provides their own typography + buttons inside `children`.
import type { ReactNode } from "react";
import {
  Modal,
  Platform,
  Pressable,
  Text,
  View,
  type GestureResponderEvent,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { BlurView } from "expo-blur";
import { SafeAreaView } from "react-native-safe-area-context";
import { colors, radii, spacing } from "../theme/tokens";
import { IconButton } from "./IconButton";

// RN Web puts the underlying DOM MouseEvent on `nativeEvent`, so at runtime
// it exposes `target` and `currentTarget`. React Native's own type for
// `nativeEvent` (NativeTouchEvent) does not declare those, so we narrow
// through a shape guard instead of casting through `unknown`.
type WebPointerTargets = {
  target: EventTarget | null;
  currentTarget: EventTarget | null;
};

function hasWebPointerTargets(value: object): value is WebPointerTargets {
  return "target" in value && "currentTarget" in value;
}

// "fullscreen" fills the viewport edge-to-edge (safe-area aware) — the right
// presentation for immersive surfaces like a chat thread on a phone, where a
// centered card is too cramped. Slide animation, no radius.
type Layout = "centered" | "bottom" | "fullscreen";
type CloseButton = false | "minimal" | "with-background";

type Props = {
  visible: boolean;
  onClose: () => void;
  layout?: Layout;
  animationType?: "none" | "fade" | "slide";
  /** Top-right close affordance.
   *  - `false` (default): no close button. Use when the dialog has its
   *    own action buttons (Cancel + Confirm) that handle dismissal.
   *  - `"minimal"`: a subtle ✕ in the top-right, no background. Use for
   *    info / detail dialogs (badge details, info popups). */
  closeButton?: CloseButton;
  /** Default `true`. Pass `false` to require an explicit action to dismiss. */
  dismissOnBackdropTap?: boolean;
  /** Optional sizing overrides for wider detail dialogs that still need shared chrome. */
  containerStyle?: StyleProp<ViewStyle>;
  /** Optional surface overrides for scrollable dialogs that need bounded height. */
  surfaceStyle?: StyleProp<ViewStyle>;
  /** Optional padding/layout overrides for custom scrollable dialog content. */
  contentStyle?: StyleProp<ViewStyle>;
  /** Optional test hook for the dismissible backdrop. */
  backdropTestID?: string;
  testID?: string;
  children: ReactNode;
};

const FROSTED_BG_WEB = "rgba(255,255,255,0.82)";
const FROSTED_FILTER_WEB = "blur(24px) saturate(1.4)";

export function Dialog({
  visible,
  onClose,
  layout = "centered",
  animationType,
  closeButton = false,
  dismissOnBackdropTap = true,
  containerStyle: containerStyleOverride,
  surfaceStyle,
  contentStyle,
  backdropTestID,
  testID,
  children,
}: Props) {
  const isCentered = layout === "centered";
  const isFullscreen = layout === "fullscreen";

  const containerStyle = isFullscreen
    ? {
        flex: 1,
        width: "100%" as const,
      }
    : isCentered
      ? {
          width: "85%" as const,
          maxWidth: 340,
          borderRadius: radii.xl + 4, // 20
          overflow: "hidden" as const,
        }
      : {
          borderTopLeftRadius: radii.xl,
          borderTopRightRadius: radii.xl,
          overflow: "hidden" as const,
        };

  const innerPadding = isFullscreen
    ? {
        flex: 1,
        paddingTop: spacing.md,
        paddingHorizontal: spacing.xl,
        paddingBottom: spacing.sm,
      }
    : isCentered
      ? {
          paddingTop: spacing.xl + spacing.xl, // 40
          paddingHorizontal: spacing.xl + spacing.sm, // 28
          paddingBottom: spacing.xl + spacing.md, // 32
        }
      : {
          paddingTop: spacing.xl,
          paddingHorizontal: spacing.xl,
          paddingBottom: spacing.xl + spacing.sm,
        };

  const dialogBody = (
    <View testID={testID} style={[innerPadding, contentStyle]}>
      {closeButton !== false && (
        <CloseAffordance variant={closeButton} onPress={onClose} />
      )}
      {children}
    </View>
  );

  // Fullscreen fills to the device edges, so keep content clear of the notch
  // and home indicator with a safe-area frame.
  const innerContent = isFullscreen ? (
    <SafeAreaView style={{ flex: 1 }} edges={["top", "bottom"]}>
      {dialogBody}
    </SafeAreaView>
  ) : (
    dialogBody
  );

  const handleBackdropPress = dismissOnBackdropTap
    ? (event: GestureResponderEvent) => {
        // Only dismiss when the tap landed on the backdrop itself. On web,
        // nested Pressables race to claim the responder, which sometimes let
        // a button click also fire the backdrop's onPress. This check keeps
        // taps inside content from bubbling into a dismiss.
        if (Platform.OS === "web") {
          const native: object = event.nativeEvent;
          if (
            hasWebPointerTargets(native) &&
            native.target !== null &&
            native.target !== native.currentTarget
          ) {
            return;
          }
        }
        onClose();
      }
    : undefined;

  return (
    <Modal
      visible={visible}
      transparent
      animationType={animationType ?? (isCentered ? "fade" : "slide")}
      onRequestClose={onClose}
    >
      <Pressable
        onPress={handleBackdropPress}
        testID={backdropTestID}
        style={{
          flex: 1,
          backgroundColor: isFullscreen ? "transparent" : "rgba(0,0,0,0.4)",
          justifyContent: isCentered ? "center" : "flex-end",
          alignItems: isCentered ? "center" : "stretch",
        }}
      >
        {/* Native responder guard: on iOS/Android, this Pressable captures
            taps inside the content so the backdrop above doesn't fire.
            On web the data-attribute check on the backdrop is what protects us. */}
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={[
            containerStyle,
            Platform.OS === "web" ? { cursor: "auto" } : null,
            containerStyleOverride,
          ]}
        >
          {Platform.OS === "web" ? (
            <View
              style={[
                {
                  backgroundColor: FROSTED_BG_WEB,
                  backdropFilter: FROSTED_FILTER_WEB,
                  WebkitBackdropFilter: FROSTED_FILTER_WEB,
                  maxHeight: "100%",
                },
                isFullscreen ? { flex: 1 } : null,
                surfaceStyle,
              ]}
            >
              {innerContent}
            </View>
          ) : (
            <BlurView
              intensity={60}
              tint="light"
              style={[
                {
                  backgroundColor: "rgba(255,255,255,0.75)",
                  maxHeight: "100%",
                },
                isFullscreen ? { flex: 1 } : null,
                surfaceStyle,
              ]}
            >
              {innerContent}
            </BlurView>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function CloseAffordance({
  variant,
  onPress,
}: {
  variant: "minimal" | "with-background";
  onPress: () => void;
}) {
  if (variant === "with-background") {
    return (
      <IconButton
        onPress={onPress}
        hitSlop={12}
        accessibilityLabel="Close"
        size={32}
        surface="soft"
        backgroundColor="rgba(0,0,0,0.06)"
        pressedBackgroundColor="rgba(0,0,0,0.06)"
        pressedOpacity={1}
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 1,
        }}
      >
        <Text style={{ fontSize: 16, color: colors.textFaint }}>✕</Text>
      </IconButton>
    );
  }
  return (
    <IconButton
      onPress={onPress}
      hitSlop={12}
      accessibilityLabel="Close"
      size={28}
      pressedOpacity={1}
      style={{
        position: "absolute",
        top: 12,
        right: 12,
        zIndex: 1,
      }}
    >
      <Text style={{ fontSize: 18, color: colors.textFaint }}>✕</Text>
    </IconButton>
  );
}
